import React, { useState } from 'react';
import { Mail, Linkedin, Twitter, Instagram, ArrowRight, TrendingUp, Target, Zap, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { mockData } from '../mock';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    business: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        business: formData.business,
        message: formData.message
      });

      if (response.data.success) {
        toast({
          title: "Message Sent!",
          description: response.data.message,
        });
        setFormData({ name: '', email: '', business: '', message: '' });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const serviceIcons = {
    0: Target,
    1: TrendingUp,
    2: RefreshCw,
    3: Zap
  };

  return (
    <div className="min-h-screen bg-[#0f0f10] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0f0f10]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="text-xl font-bold tracking-tight">
            <span className="text-[#d9fb06]">COPY</span>STRATEGIST
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-sm hover:text-[#d9fb06] transition-colors">Services</a>
            <a href="#results" className="text-sm hover:text-[#d9fb06] transition-colors">Results</a>
            <a href="#contact" className="text-sm hover:text-[#d9fb06] transition-colors">Contact</a>
          </nav>
          <a href="#contact">
            <Button className="bg-[#d9fb06] text-black hover:bg-[#d9fb06]/90 rounded-full font-semibold">
              Get Started
            </Button>
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={mockData.hero.backgroundImage}
            alt="Workspace"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-black leading-[0.9] mb-8">
              Writing good copy is not enough until you have
              <span className="text-[#d9fb06]"> the right strategy.</span>
              <br />
              <span className="text-5xl md:text-6xl">Period.</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-6 font-medium">
              {mockData.hero.subtitle}
            </p>
            
            <p className="text-lg md:text-xl text-[#d9fb06] mb-10 font-semibold">
              {mockData.hero.results}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#contact">
                <Button className="bg-[#d9fb06] text-black hover:bg-[#d9fb06]/90 rounded-full px-8 py-6 text-lg font-semibold">
                  {mockData.hero.ctaText} <ArrowRight className="ml-2" />
                </Button>
              </a>
              <a href="#results">
                <Button variant="outline" className="border-[#d9fb06] text-[#d9fb06] hover:bg-[#d9fb06] hover:text-black rounded-full px-8 py-6 text-lg font-semibold">
                  See Results
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-[#1a1a1c]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={mockData.about.image}
                alt="Strategist"
                className="w-full max-w-md mx-auto rounded-2xl shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-black mb-6 leading-tight">
                {mockData.about.name}
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed">
                {mockData.about.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-[#0f0f10]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              What I <span className="text-[#d9fb06]">Build</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Revenue-focused email systems that turn subscribers into customers
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {mockData.services.map((service, index) => {
              const Icon = serviceIcons[index];
              return (
                <Card key={service.id} className="bg-[#1a1a1c] border-white/10 hover:border-[#d9fb06]/50 transition-all duration-300 hover:transform hover:scale-[1.02]">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-[#d9fb06]/10 rounded-lg">
                        <Icon className="w-6 h-6 text-[#d9fb06]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold mb-3 text-white">{service.title}</h3>
                        <p className="text-gray-400 mb-4 leading-relaxed">{service.description}</p>
                        <div className="inline-flex items-center gap-2 text-[#d9fb06] font-semibold text-sm">
                          <TrendingUp className="w-4 h-4" />
                          {service.results}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats/Results Section */}
      <section id="results" className="py-24 bg-[#d9fb06] text-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              Results That Speak
            </h2>
            <p className="text-xl max-w-2xl mx-auto opacity-80">
              Real numbers from real ecommerce brands
            </p>
          </div>

          <div className="grid sm:grid-cols-1 lg:grid-cols-3 gap-8">
            {mockData.stats.map((stat) => (
              <div key={stat.id} className="text-center">
                <div className="text-6xl md:text-7xl font-black mb-2">{stat.number}</div>
                <div className="text-xl font-bold mb-2">{stat.label}</div>
                <div className="text-sm opacity-80">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-[#1a1a1c]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              Client <span className="text-[#d9fb06]">Wins</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockData.testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-[#0f0f10] border-white/10">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <div className="text-3xl font-black text-[#d9fb06] mb-4">{testimonial.result}</div>
                    <p className="text-gray-300 italic leading-relaxed">"{testimonial.quote}"</p>
                  </div>
                  <div>
                    <div className="font-bold">{testimonial.author}</div>
                    <div className="text-sm text-gray-500">{testimonial.company}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-[#0f0f10]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              Ready to <span className="text-[#d9fb06]">Boost Revenue?</span>
            </h2>
            <p className="text-xl text-gray-400">
              Let's talk about your email strategy
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-12">
            {/* Contact Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-[#1a1a1c] border-white/10 text-white placeholder:text-gray-500"
                />
                <Input
                  name="email"
                  type="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-[#1a1a1c] border-white/10 text-white placeholder:text-gray-500"
                />
                <Input
                  name="business"
                  placeholder="Your Business Name"
                  value={formData.business}
                  onChange={handleChange}
                  className="bg-[#1a1a1c] border-white/10 text-white placeholder:text-gray-500"
                />
                <Textarea
                  name="message"
                  placeholder="Tell me about your email marketing goals..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="bg-[#1a1a1c] border-white/10 text-white placeholder:text-gray-500"
                />
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#d9fb06] text-black hover:bg-[#d9fb06]/90 rounded-full py-6 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Direct Contact */}
            <div className="flex flex-col justify-center space-y-6">
              <div>
                <h3 className="text-2xl font-bold mb-4">Or reach out directly:</h3>
                <a 
                  href={`mailto:${mockData.contact.email}`}
                  className="flex items-center gap-3 text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors text-lg"
                >
                  <Mail className="w-5 h-5" />
                  {mockData.contact.email}
                </a>
              </div>

              <div>
                <h4 className="text-lg font-semibold mb-3">Connect with me:</h4>
                <div className="flex gap-4">
                  {mockData.contact.socialLinks.map((link) => (
                    <a
                      key={link.platform}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-[#1a1a1c] hover:bg-[#d9fb06] hover:text-black rounded-full transition-all duration-300"
                    >
                      {link.platform === 'LinkedIn' && <Linkedin className="w-5 h-5" />}
                      {link.platform === 'Instagram' && <Instagram className="w-5 h-5" />}
                      {link.platform === 'Twitter' && <Twitter className="w-5 h-5" />}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#0a0a0b] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-500">
              © 2025 CopyStrategist. All rights reserved.
            </div>
            <div className="text-sm text-gray-500">
              Conversion-focused email copy that drives revenue.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
